import { Container, GridItem, Flex, Spinner, Grid } from '@chakra-ui/react';
import { format } from 'date-fns';
import { Formik, Form } from 'formik';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/Button';
import { FormikDateRangePicker, FormikSelectAsyncPaginate } from 'components/Formik';
import Title from 'components/Title';
import { fetchPaginatedJobs } from 'hooks/jobs/useFetchPaginatedJobs';
import { useDownloadReportByJobsTotalOnlyCSV } from 'hooks/reports/useDownloadReportByJobsTotalOnlyCSV';
import { fetchPaginatedTasks } from 'hooks/task/useFetchPaginatedTasks';
import { getAsyncSelectLoadOptions } from 'utils/helpers';

import {
  JobOption,
  TaskOption,
  ReportFormData,
  initialReportByJobsTotalOnlyFormData,
  reportByJobsTotalOnlyValidationSchema,
} from './schema';

const ReportByJobsTotalOnly: React.FC = () => {
  const { t } = useTranslation(['common', 'reports']);
  const formikRef = useRef<any>();

  const { mutateAsync: mutateAsyncDownloadCSV, isLoading: isDownloadCSVLoading } =
    useDownloadReportByJobsTotalOnlyCSV();

  const [isDownloadingCSV, setIsDownloadingCSV] = useState(false);

  // Job load options
  const jobLoadOptions = async (search: string, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchPaginatedJobs, {
      search,
      page,
      job_status: 1,
    });
  };

  // Task load options - filtered by selected jobs
  const taskLoadOptions = (page: number, search: string, jobIds: JobOption[] | null) => {
    if (jobIds && jobIds.length) {
      const jobList = jobIds.map((item: JobOption) => item.id).join(',');
      return getAsyncSelectLoadOptions(fetchPaginatedTasks, {
        page,
        search,
        task_status: 1,
        jobId: jobList,
      });
    } else {
      return getAsyncSelectLoadOptions(fetchPaginatedTasks, {
        page,
        search,
        task_status: 1,
        jobId: '0',
      });
    }
  };

  // Handle job selection change - reset task when jobs change
  const handleJobChangeCallback = (
    _: JobOption[] | null, // Using underscore to indicate unused parameter
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    // Reset task selection when jobs change
    setFieldValue('task_id', null);
  };

  const buildPostData = (values: ReportFormData) => {
    const postData: any = {
      start_date: values.date_range[0] ? format(values.date_range[0], 'yyyy-MM-dd') : '',
      end_date: values.date_range[1] ? format(values.date_range[1], 'yyyy-MM-dd') : '',
    };

    // Add job_ids only if jobs are selected
    if (Array.isArray(values.job_ids) && values.job_ids.length > 0) {
      postData.job_ids = values.job_ids.map(job => job.id);
    }

    // Add task_id if task is selected
    if (values.task_id) {
      postData.task_id = values.task_id.id;
    }

    return postData;
  };

  const handleCSVDownload = async (values: ReportFormData) => {
    setIsDownloadingCSV(true);
    try {
      const postData = buildPostData(values);
      await mutateAsyncDownloadCSV(postData);
    } catch (error) {
      console.error('CSV Download Error:', error);
    } finally {
      setIsDownloadingCSV(false);
    }
  };

  return (
    <div>
      <Title>{t('reports:reportByJobsTotalOnly')}</Title>
      <Container mt="10" maxW="container.xl" p="0">
        <Formik
          initialValues={initialReportByJobsTotalOnlyFormData}
          validationSchema={reportByJobsTotalOnlyValidationSchema}
          onSubmit={values => {
            handleCSVDownload(values);
          }}
        >
          {formikBag => {
            formikRef.current = formikBag;
            const { values, setFieldValue } = formikBag;
            return (
              <Form autoComplete="off">
                <Grid
                  templateColumns={{
                    base: '1fr',
                    sm: 'repeat(2, 1fr)',
                    lg: 'repeat(3, 1fr)',
                  }}
                  gap="4"
                  alignContent="flex-start"
                >
                  {/* Date Range */}
                  <GridItem>
                    <FormikDateRangePicker label={t('common:dateRange')} name="date_range" />
                  </GridItem>

                  {/* Jobs Selection */}
                  <GridItem>
                    <FormikSelectAsyncPaginate
                      name="job_ids"
                      label={t('common:jobName')}
                      loadOptions={jobLoadOptions}
                      selectProps={{
                        placeholder: t('common:allJobs'),
                        isMulti: true,
                        getOptionLabel: (option: JobOption) => option.name,
                        getOptionValue: (option: JobOption) => option.id,
                      }}
                      onChangeCallback={(value: JobOption[] | null) => handleJobChangeCallback(value, setFieldValue)}
                    />
                  </GridItem>

                  {/* Task Selection */}
                  <GridItem>
                    <FormikSelectAsyncPaginate
                      name="task_id"
                      label={t('common:taskName')}
                      loadOptions={(search: string, _: any, { page }: any) => {
                        return taskLoadOptions(page, search, values.job_ids);
                      }}
                      selectProps={{
                        getOptionLabel: (option: TaskOption) => option.name,
                        getOptionValue: (option: TaskOption) => option.id,
                        placeholder: t('common:selectTask'),
                        isDisabled: !values.job_ids?.length,
                        cacheUniqs: [values.job_ids],
                      }}
                    />
                  </GridItem>
                </Grid>

                {/* Export Button */}
                <Flex mt="8" justify="center" alignItems="center">
                  <Button
                    type="submit"
                    colorScheme="secondary"
                    isLoading={isDownloadCSVLoading || isDownloadingCSV}
                    minW="200px"
                    size="lg"
                  >
                    {isDownloadingCSV || isDownloadCSVLoading ? (
                      <>
                        <Spinner mr="2" size="sm" />
                        Generating...
                      </>
                    ) : (
                      t('reports:exportAsCSV')
                    )}
                  </Button>
                </Flex>
              </Form>
            );
          }}
        </Formik>
      </Container>
    </div>
  );
};

export default ReportByJobsTotalOnly;
